import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings, ArrowLeft, Bell, Volume2, Globe, Navigation, Moon, Smartphone, Vibrate, BellRing, MapPin, Clock, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";

const AppSettingsPage = () => {
  const [settings, setSettings] = useState({
    push_notifications: true,
    sound_alerts: true,
    vibration: true,
    new_request_sound: true,
    auto_start_online: false,
    dark_mode: false,
    auto_accept_jobs: false,
  });
  const [language, setLanguage] = useState("en");
  const [navPref, setNavPref] = useState("mapbox");
  const [jobRadius, setJobRadius] = useState([15]);
  const [workStart, setWorkStart] = useState("08:00");
  const [workEnd, setWorkEnd] = useState("20:00");

  const toggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    toast.success("Setting updated");
  };

  const notifSettings = [
    { key: "push_notifications", icon: Bell, label: "Push Notifications", desc: "Receive push alerts for new job requests and updates" },
    { key: "new_request_sound", icon: BellRing, label: "New Request Alert", desc: "Play a distinct sound when a new job request arrives" },
    { key: "sound_alerts", icon: Volume2, label: "Sound Effects", desc: "Play sounds for actions like accepting jobs and messages" },
    { key: "vibration", icon: Vibrate, label: "Vibration", desc: "Vibrate on new notifications and alerts" },
  ];

  const appSettings = [
    { key: "auto_start_online", icon: Smartphone, label: "Auto-Start Online", desc: "Automatically set status to Online when you open the app" },
    { key: "dark_mode", icon: Moon, label: "Dark Mode", desc: "Use dark theme throughout the application" },
  ];

  const languages = [
    { value: "en", label: "English" },
    { value: "fr", label: "French" },
    { value: "es", label: "Spanish" },
    { value: "zh", label: "Chinese" },
    { value: "ar", label: "Arabic" },
  ];

  const navOptions = [
    { value: "mapbox", label: "Mapbox" },
    { value: "google", label: "Google Maps" },
    { value: "apple", label: "Apple Maps" },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="app-settings-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2 mb-6">
            <Settings className="w-6 h-6 text-stone-600" /> App Settings
          </h1>

          {/* Notifications */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Notifications & Sound</p>
            <div className="space-y-2">
              {notifSettings.map((item) => (
                <div key={item.key} className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center justify-between" data-testid={`setting-${item.key}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><item.icon className="w-4 h-4 text-stone-600" /></div>
                    <div className="min-w-0">
                      <Label className="font-medium text-emerald-950">{item.label}</Label>
                      <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                  <Switch checked={settings[item.key]} onCheckedChange={() => toggle(item.key)} className="shrink-0 ml-3" />
                </div>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Language</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="language-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center"><Globe className="w-4 h-4 text-stone-600" /></div>
                <Label className="font-medium text-emerald-950">Language</Label>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {languages.map((lang) => (
                  <button key={lang.value} onClick={() => { setLanguage(lang.value); toast.success(`Language set to ${lang.label}`); }}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${language === lang.value ? "bg-emerald-600 text-white" : "bg-stone-50 text-stone-600 hover:bg-stone-100"}`}
                    data-testid={`lang-${lang.value}`}>
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Navigation</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="navigation-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center"><Navigation className="w-4 h-4 text-stone-600" /></div>
                <div>
                  <Label className="font-medium text-emerald-950">Navigation App</Label>
                  <p className="text-xs text-stone-500">Preferred map service for directions</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {navOptions.map((opt) => (
                  <button key={opt.value} onClick={() => { setNavPref(opt.value); toast.success(`Navigation set to ${opt.label}`); }}
                    className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${navPref === opt.value ? "bg-emerald-600 text-white" : "bg-stone-50 text-stone-600 hover:bg-stone-100"}`}
                    data-testid={`nav-${opt.value}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* App Preferences */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">App Preferences</p>
            <div className="space-y-2">
              {appSettings.map((item) => (
                <div key={item.key} className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center justify-between" data-testid={`setting-${item.key}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><item.icon className="w-4 h-4 text-stone-600" /></div>
                    <div className="min-w-0">
                      <Label className="font-medium text-emerald-950">{item.label}</Label>
                      <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                  <Switch checked={settings[item.key]} onCheckedChange={() => toggle(item.key)} className="shrink-0 ml-3" />
                </div>
              ))}
            </div>
          </div>

          {/* Work Preferences */}
          <div>
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Work Preferences</p>
            <div className="space-y-2">
              {/* Job Radius */}
              <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="setting-job-radius">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-emerald-600" /></div>
                  <div className="flex-1">
                    <Label className="font-medium text-emerald-950">Job Radius</Label>
                    <p className="text-xs text-stone-500 mt-0.5">Only receive job requests within this distance from you</p>
                  </div>
                  <span className="text-sm font-semibold text-emerald-700 tabular-nums" data-testid="job-radius-value">{jobRadius[0]} km</span>
                </div>
                <Slider value={jobRadius} onValueChange={(v) => { setJobRadius(v); }} onValueCommit={(v) => toast.success(`Job radius set to ${v[0]} km`)} min={2} max={50} step={1} className="mx-1" data-testid="job-radius-slider" />
                <div className="flex justify-between text-[10px] text-stone-400 mt-1.5 px-1">
                  <span>2 km</span><span>25 km</span><span>50 km</span>
                </div>
              </div>

              {/* Work Hours */}
              <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="setting-work-hours">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center shrink-0"><Clock className="w-4 h-4 text-amber-600" /></div>
                  <div>
                    <Label className="font-medium text-emerald-950">Work Hours</Label>
                    <p className="text-xs text-stone-500 mt-0.5">Outside these hours you'll auto-go offline</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-stone-500">Start</Label>
                    <input type="time" value={workStart} onChange={(e) => { setWorkStart(e.target.value); toast.success("Work start time updated"); }} className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="work-start-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">End</Label>
                    <input type="time" value={workEnd} onChange={(e) => { setWorkEnd(e.target.value); toast.success("Work end time updated"); }} className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="work-end-input" />
                  </div>
                </div>
              </div>

              {/* Auto-Accept Jobs */}
              <div className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center justify-between" data-testid="setting-auto_accept_jobs">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 bg-violet-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><Zap className="w-4 h-4 text-violet-600" /></div>
                  <div className="min-w-0">
                    <Label className="font-medium text-emerald-950">Auto-Accept Jobs</Label>
                    <p className="text-xs text-stone-500 mt-0.5">Automatically accept new requests that match your radius and hours</p>
                  </div>
                </div>
                <Switch checked={settings.auto_accept_jobs} onCheckedChange={() => toggle("auto_accept_jobs")} className="shrink-0 ml-3" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AppSettingsPage;
